import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Printer, Download } from 'lucide-react';
import { API_BASE_URL, createApiUrlWithPrefix, getUserImageUrl } from '../config/api';
import axios from 'axios';

const UserBadgeModal = ({ isOpen, onClose, user }) => {
  const [userGuid, setUserGuid] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserGuid();
    }
  }, [isOpen, user]);

  const fetchUserGuid = async () => {
    if (user.userGuid) {
      setUserGuid(user.userGuid);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        createApiUrlWithPrefix(`users/${user.id}/qr-code`),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setUserGuid(response.data.userGuid || response.data.qrCode || user.userGuid);
    } catch (error) {
      console.error('Error fetching user GUID:', error);
      // Fallback to user ID if GUID not available
      setUserGuid(user.userGuid || `EC_${user.id}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    const qrData = userGuid || user.userGuid || `EC_${user.id}`;
    const birthDate = user.birthDate && user.birthDate !== null
      ? new Date(user.birthDate).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      : 'Non fourni';

    // Generate QR code as data URL
    const QRCode = await import('qrcode');
    const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
      width: 140,
      margin: 1,
      color: {
        dark: '#2c3e50',
        light: '#ffffff'
      }
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Badge - ${user.firstName} ${user.lastName}</title>
          <style>
            @media print {
              @page {
                size: A4 landscape;
                margin: 0;
              }
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            .badge {
              width: 500px;
              height: 420px;
              border: 3px solid #2c3e50;
              margin: 0 auto;
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              border-radius: 15px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              position: relative;
              overflow: hidden;
            }
            .header {
              background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
              color: white;
              padding: 15px 25px;
              text-align: center;
              font-weight: bold;
              font-size: 18px;
              letter-spacing: 1px;
            }
            .content {
              padding: 25px;
              display: flex;
              gap: 20px;
              height: calc(100% - 70px);
            }
            .left-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            .user-photo {
              width: 100px;
              height: 100px;
              border-radius: 50%;
              border: 3px solid #3b82f6;
              object-fit: cover;
              margin-bottom: 15px;
            }
            .user-photo-placeholder {
              width: 100px;
              height: 100px;
              border-radius: 50%;
              border: 3px solid #3b82f6;
              background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 32px;
              color: #3b82f6;
              margin-bottom: 15px;
            }
            .user-name {
              font-size: 22px;
              font-weight: bold;
              color: #2c3e50;
              margin-bottom: 8px;
            }
            .user-details {
              font-size: 14px;
              color: #5a6c7d;
              line-height: 1.6;
            }
            .detail-item {
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .right-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .qr-container {
              width: 160px;
              height: 160px;
              border: 2px solid #3b82f6;
              border-radius: 10px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: white;
              margin-bottom: 15px;
              padding: 10px;
            }
            .qr-code {
              width: 140px;
              height: 140px;
            }
            .qr-code img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
            .qr-label {
              font-size: 11px;
              color: #3b82f6;
              font-weight: bold;
              text-align: center;
              margin-bottom: 8px;
            }
            .qr-guid {
              font-size: 9px;
              color: #6b7280;
              text-align: center;
              word-break: break-all;
              margin-top: 5px;
            }
            .role-badge {
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: white;
              padding: 6px 16px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            @media print {
              body {
                margin: 0;
                padding: 10px;
              }
              .badge {
                margin: 0;
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="badge">
            <div class="header">MEMBRE DU CLUB HIPPIQUE</div>
            <div class="content">
              <div class="left-section">
                ${(user.photoPath || user.imagePath) ? 
                  `<img src="${API_BASE_URL}/${user.photoPath || user.imagePath}" alt="User Photo" class="user-photo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />` : 
                  ''
                }
                <div class="user-photo-placeholder" style="${(user.photoPath || user.imagePath) ? 'display: none;' : 'display: flex;'}">
                  ${user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div class="user-name">${user.firstName || ''} ${user.lastName || ''}</div>
                <div class="user-details">
                  <div class="detail-item">
                    <strong>Date de naissance:</strong> ${birthDate}
                  </div>
                  ${user.bloodType ? `<div class="detail-item"><strong>Groupe sanguin:</strong> ${user.bloodType}</div>` : ''}
                  ${user.phoneNumber ? `<div class="detail-item"><strong>Téléphone:</strong> ${user.phoneNumber}</div>` : ''}
                </div>
                <div class="role-badge">${getRoleName(user.role)}</div>
              </div>
              <div class="right-section">
                <div class="qr-container">
                  <div class="qr-label">Code QR Personnel</div>
                  <div class="qr-code">
                    <img src="${qrCodeDataUrl}" alt="QR Code" />
                  </div>
                  <div class="qr-guid">${qrData}</div>
                </div>
              </div>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Generate QR code as data URL for print
  const generateQRCodeDataURL = async (text) => {
    try {
      // Use qrcode library to generate QR code as data URL
      const QRCode = await import('qrcode');
      const dataUrl = await QRCode.toDataURL(text, {
        width: 140,
        margin: 1,
        color: {
          dark: '#2c3e50',
          light: '#ffffff'
        }
      });
      return dataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const getRoleName = (role) => {
    const roleMap = {
      0: 'Cavalier',
      1: 'Propriétaire',
      2: 'Entraîneur',
      3: 'Admin',
      4: 'Super Admin',
      5: 'Temporaire'
    };
    return roleMap[role] || roleMap[role?.toString()] || 'Membre';
  };

  if (!isOpen || !user) return null;

  const qrData = userGuid || user.userGuid || `EC_${user.id}`;
  const birthDate = user.birthDate && user.birthDate !== null
    ? new Date(user.birthDate).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : 'Non fourni';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Badge - {user.firstName} {user.lastName}</h2>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Imprimer"
              >
                <Printer className="h-5 w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Badge Preview */}
          <div className="flex justify-center">
            <div className="badge-preview w-[500px] h-[420px] border-3 border-gray-800 rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-4 text-center font-bold text-lg tracking-wide">
                MEMBRE DU CLUB HIPPIQUE
              </div>

              {/* Content */}
              <div className="p-6 flex gap-5 h-[calc(100%-70px)]">
                {/* Left Section */}
                <div className="flex-1 flex flex-col gap-3">
                  {getUserImageUrl(user) ? (
                    <img
                      src={getUserImageUrl(user)}
                      alt="User Photo"
                      className="w-24 h-24 rounded-full border-3 border-blue-600 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-24 h-24 rounded-full border-3 border-blue-600 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center font-bold text-3xl text-blue-600 ${getUserImageUrl(user) ? 'hidden' : 'flex'}`}
                  >
                    {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  
                  <div className="text-xl font-bold text-gray-800">
                    {user.firstName || ''} {user.lastName || ''}
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-2">
                    <div className="flex items-center gap-2">
                      <strong>Date de naissance:</strong> {birthDate}
                    </div>
                    {user.bloodType && (
                      <div className="flex items-center gap-2">
                        <strong>Groupe sanguin:</strong> {user.bloodType}
                      </div>
                    )}
                    {user.phoneNumber && (
                      <div className="flex items-center gap-2">
                        <strong>Téléphone:</strong> {user.phoneNumber}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide inline-block mt-2">
                    {getRoleName(user.role)}
                  </div>
                </div>

                {/* Right Section - QR Code */}
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="w-40 h-40 border-2 border-blue-600 rounded-lg bg-white p-3 flex flex-col items-center justify-center">
                    <div className="text-xs font-bold text-blue-600 mb-2 text-center">Code QR Personnel</div>
                    {loading ? (
                      <div className="w-32 h-32 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <QRCodeSVG
                        value={qrData}
                        size={128}
                        level="H"
                        includeMargin={false}
                        fgColor="#2c3e50"
                        bgColor="#ffffff"
                      />
                    )}
                    <div className="text-[8px] text-gray-500 text-center mt-2 break-all px-1">
                      {qrData}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserBadgeModal;

